import { Inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from '@auth0/auth0-angular';
import jwt_decode from 'jwt-decode';
import { LoggingService } from '@cigna/shared/angular/logging-util';
import {
  CORE_WINDOW_CONFIG,
  CoreWindowConfig,
  WebviewMessageBus,
  WindowService,
} from '@cigna/shared/angular/core/window-util';

import {
  Authenticator,
  OidcResult,
  TokenBody,
  AuthError,
} from '@cigna/shared/angular/auth/oidc-util';
import { map, tap } from 'rxjs/operators';
import { AUTH0_FLOW_CONFIG, Auth0FlowConfig } from './auth0-flow-config';
import { defer, firstValueFrom } from 'rxjs';

const TOKEN_COOKIE = 'accessToken';

@Injectable({
  providedIn: 'root',
})
export class Auth0FlowService implements Authenticator {
  constructor(
    private _logger: LoggingService,
    private _auth0: AuthService,
    @Inject(AUTH0_FLOW_CONFIG) private config: Auth0FlowConfig,
    @Inject(CORE_WINDOW_CONFIG) private _windowConfig: CoreWindowConfig,
    private _cookie: CookieService,
    private _window: WindowService,
    private _webviewMessageBus: WebviewMessageBus,
  ) {}

  authenticate(authData?: { previousAccessToken: TokenBody }) {
    if (this.isImpersonator(authData?.previousAccessToken)) {
      throw new AuthError({ error: 'login_required' });
    }
    return defer(() => this.silentSignIn());
  }

  async silentSignIn(): Promise<OidcResult> {
    if (this._windowConfig.isWebview) {
      // authentication in device
      if (this.canGetTokensFromNative()) {
        return this.getTokensFromNative();
      }

      // authentication in browser, using cached accessToken
      if (this.config.cookieStorage) {
        try {
          // checking if user has an active session already
          const auth = this.getInitialData();
          if (auth) {
            return auth;
          }
        } catch {
          /* ignore the default, Router-based redirect as webview may live in another app */
        }
        this.setDeepLink();
        this._window.location.assign(this.config.postLogoutRedirectUri);
      }
    }
    try {
      return await firstValueFrom(
        this._auth0
          .getAccessTokenSilently({
            cacheMode: 'off',
            ...(this.config.hasAuthParamChange && {
              authorizationParams: {
                audience: this.config.audience,
              },
            }),
          })
          .pipe(
            tap((accessToken) => {
              this.setCookieStorage(accessToken);
            }),
            map((accessToken) => tokenToOidc(accessToken)),
          ),
      );
    } catch (e) {
      this.setDeepLink();
      this._logger.error(e, 'error initiating auth0 flow');
      throw new AuthError(e);
    }
  }

  loginWithRedirect(returnTo: string) {
    return this._auth0.loginWithRedirect({
      appState: { returnTo },
    });
  }

  logout(returnTo: string) {
    this._auth0.logout({
      logoutParams: {
        returnTo,
      },
    });
  }

  signOut(
    params?: Record<string, string>,
    redirectUrl: string = this.config.postLogoutRedirectUri,
  ) {
    this.logout(redirectUrl);
  }

  getInitialData() {
    if (this.config.cookieStorage) {
      const accessToken = this._cookie.get(TOKEN_COOKIE);
      if (accessToken) {
        try {
          return tokenToOidc(accessToken);
        } catch (e) {
          this._logger.warn(e);
          this.deleteAccessTokenCookie();
        }
      }
      throw new AuthError({ error: 'login_required' });
    }
  }

  setDeepLink() {
    this.setOriginalUri(
      this._window.location.pathname + this._window.location.search,
    );
  }

  getOriginalUri() {
    return this._window.sessionStorage.referrerPath;
  }

  setOriginalUri(uri: string) {
    this._window.sessionStorage.referrerPath = uri;
  }

  deleteAccessTokenCookie() {
    if (this.config.cookieStorage) {
      this._cookie.delete(TOKEN_COOKIE, '/', this.getCookieDomain());
    }
  }

  setCookieStorage(accessToken: string) {
    if (this.config.cookieStorage) {
      this._cookie.set(
        TOKEN_COOKIE,
        accessToken,
        new Date(Date.now() + 10 * 60 * 1000), // +10 minutes
        '/',
        this.getCookieDomain(),
        true,
      );
    }
  }

  getCookieDomain() {
    return undefined;
  }

  isImpersonator(token?: TokenBody) {
    if (!token) {
      return false;
    }
    return (
      token['agent.operatorId'] &&
      token['agent.operatorRole'] === 'SUB_IMP_LVL_1'
    );
  }

  canGetTokensFromNative(): boolean {
    return (
      // !!this.config.retrieveTokensFromNative &&
      this._webviewMessageBus.hasRetrieveAccessTokenMethod()
    );
  }

  getTokensFromNative() {
    this._logger.info('calling the app to get the tokens');

    this._webviewMessageBus.getTokens();
    return new Promise<OidcResult>((resolve, reject) => {
      const listener = (event: MessageEvent) => {
        this._logger.info('received a message', event.data);
        const data = event.data;
        if (typeof data !== 'object') {
          return;
        }
        if ('webviewAccessToken' in data) {
          this._logger.info(
            'received a message with webviewAccessToken',
            data.webviewAccessToken,
          );

          try {
            const oidcResult = tokenToOidc(data.webviewAccessToken);
            this.setCookieStorage(data.webviewAccessToken);
            resolve(oidcResult);
          } catch (e) {
            reject(e);
          }
          this._window.removeEventListener('message', listener);
        } else if ('webviewError' in data) {
          this._logger.info(
            'received a message with webviewError',
            data.webviewError,
          );

          reject(data.webviewError);
          this._window.removeEventListener('message', listener);
        }
      };

      this._window.addEventListener('message', listener);
    });
  }
}

function tokenToOidc(token: string): OidcResult {
  const accessTokenBody = jwt_decode(token);
  return {
    accessToken: token,
    accessTokenBody,
    idTokenBody: {},
  } as unknown as OidcResult;
}
